import { ChevronDown } from '@/components/ui/icons';
import { useState, useEffect, useRef, useMemo } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Model } from '@shared/types';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { ModelConfig } from '../types/misc.ts';
import { useConversation } from '@/contexts/ConversationContext';

interface ModelSelectorProps {
  models: ModelConfig[];
  selectedModel: string;
  onModelChange: (modelId: Model) => void;
  disabled?: boolean;
  className?: string;
  type?: 'parametric' | 'imagetocad'; // Optional type prop that takes precedence over conversation context
  focused?: boolean; // New prop to indicate if text area is focused
  useShortLabel?: boolean; // When true, show shortName on desktop (editor page)
  // When set, this string is used as the trigger label instead of the
  // selected model's name. Pass '' to render a blank trigger.
  displayLabel?: string;
  // When set, overrides the default "Select A.I. Model" tooltip. Used for
  // tabs where the LLM choice is intentionally hidden from the user
  // (NASA SUPIN, Implicit Primitives) — the tooltip explains why instead
  // of just leaving them to wonder.
  lockedTooltip?: string;
}

export function ModelSelector({
  models,
  selectedModel,
  onModelChange,
  className,
  disabled,
  type,
  focused = false,
  useShortLabel = false,
  displayLabel,
  lockedTooltip,
}: ModelSelectorProps) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { conversation } = useConversation();

  // Use provided type prop or fall back to conversation context
  const currentType = type || conversation.type;

  // Track previous model name for slide animation
  const [prevModelName, setPrevModelName] = useState<string | null>(null);
  const [prevModelShortName, setPrevModelShortName] = useState<string | null>(
    null,
  );
  const [isSliding, setIsSliding] = useState(false);
  const [slideDirection, setSlideDirection] = useState<'up' | 'down'>('up');

  const selectedModelConfig = models.find((m) => m.id === selectedModel);

  const groupedModels = useMemo(() => {
    const families: { family: string; models: ModelConfig[] }[] = [];
    const standalone: ModelConfig[] = [];
    const familyMap = new Map<string, ModelConfig[]>();
    const familyOrder: string[] = [];
    for (const model of models) {
      if (model.family) {
        if (!familyMap.has(model.family)) {
          familyMap.set(model.family, []);
          familyOrder.push(model.family);
        }
        familyMap.get(model.family)!.push(model);
      } else {
        standalone.push(model);
      }
    }
    for (const family of familyOrder) {
      families.push({ family, models: familyMap.get(family)! });
    }
    return { families, standalone };
  }, [models]);

  // Store previous selected model name and type
  const prevNameRef = useRef<string | undefined>(selectedModelConfig?.name);
  const prevShortNameRef = useRef<string | undefined>(
    selectedModelConfig?.shortName ?? selectedModelConfig?.name,
  );
  const prevTypeRef = useRef<'parametric' | 'imagetocad' | undefined>(
    currentType,
  );
  const recentTypeChangeRef = useRef<number>(0); // Timestamp of last type change

  // ---------------------------------------------------------------------------
  // Focus management
  // ---------------------------------------------------------------------------
  // Radix will always move focus back to the trigger after the dropdown closes.
  // When the user opened the menu via *keyboard* this is great for accessibility
  // because they can keep tabbing.  But when the user opened the menu with a
  // *pointer* (mouse / touch), that behaviour leaves an unwanted focus outline
  // "stuck" on the button.  We therefore:
  //   1. Track *how* the menu was opened (pointer vs. keyboard).
  //   2. If it was opened via pointer, cancel the automatic focus-return and
  //      blur the trigger so no outline is shown.
  // ---------------------------------------------------------------------------
  const openedWithPointerRef = useRef(false);

  // Ref to the trigger button so we can blur it if needed.
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  // Trigger slide animation when selected model changes
  useEffect(() => {
    if (
      prevNameRef.current &&
      prevNameRef.current !== selectedModelConfig?.name
    ) {
      setPrevModelName(prevNameRef.current);
      setPrevModelShortName(
        prevShortNameRef.current ?? prevNameRef.current ?? null,
      );
      setIsSliding(true);

      // Check if type changed (mode switch)
      const typeChanged = prevTypeRef.current !== currentType;
      const now = Date.now();
      const recentTypeChange = now - recentTypeChangeRef.current < 100; // Within 100ms

      if (typeChanged) {
        // Mode switching logic: parametric = down, creative = up
        const direction = currentType === 'parametric' ? 'down' : 'up';
        setSlideDirection(direction);
        recentTypeChangeRef.current = now;
      } else if (!recentTypeChange) {
        // Within same mode: preserve existing index-based logic
        // But only if we haven't had a recent type change
        const prevIndex = models.findIndex(
          (m) => m.name === prevNameRef.current,
        );
        const newIndex = models.findIndex((m) => m.id === selectedModel);
        if (prevIndex !== -1 && newIndex !== -1) {
          const direction = newIndex > prevIndex ? 'up' : 'down';
          setSlideDirection(direction);
        }
      }
    }

    prevNameRef.current = selectedModelConfig?.name;
    prevShortNameRef.current =
      selectedModelConfig?.shortName ?? selectedModelConfig?.name;
    prevTypeRef.current = currentType;
  }, [
    selectedModelConfig?.name,
    selectedModelConfig?.shortName,
    currentType,
    models,
    selectedModel,
  ]);

  const handleSlideEnd = () => {
    setPrevModelName(null);
    setPrevModelShortName(null);
    setIsSliding(false);
  };

  return (
    <Tooltip>
      <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <Button
              ref={triggerRef}
              // Record that the menu was opened with a pointer so we can adjust
              // focus handling on close.
              onPointerDown={() => {
                openedWithPointerRef.current = true;
              }}
              // Keyboard interaction should *not* suppress the focus outline.
              onKeyDown={() => {
                openedWithPointerRef.current = false;
              }}
              variant="ghost"
              className={cn(
                'flex h-8 w-auto items-center gap-1.5 rounded-lg px-3 text-sm transition-all duration-200 hover:border-[#107d40]/50 hover:bg-[#107d40]/10',
                focused
                  ? 'text-white hover:text-white'
                  : 'text-telekinesis-text-secondary hover:text-telekinesis-text-primary',
                isDropdownOpen &&
                  (focused
                    ? 'bg-telekinesis-neutral-800 text-white'
                    : 'bg-telekinesis-neutral-800 text-telekinesis-text-primary'),
                // When locked, keep the label fully legible — the disabled cue
                // is the dimmed chevron below, not a greyed-out value. (Cancels
                // the Button's base disabled:opacity-50.)
                'disabled:opacity-100',
                className,
              )}
              disabled={!!disabled}
            >
              <span className="relative inline-grid items-center overflow-hidden text-right font-normal">
                {/* Previous name sliding out */}
                {prevModelName && (
                  <span
                    style={{ gridColumn: 1, gridRow: 1 }}
                    className={`block ${
                      slideDirection === 'up'
                        ? 'slide-out-up'
                        : 'slide-out-down'
                    }`}
                    onAnimationEnd={handleSlideEnd}
                  >
                    <span className="md:hidden">
                      {prevModelShortName ?? prevModelName}
                    </span>
                    <span className="hidden md:inline">
                      {useShortLabel
                        ? (prevModelShortName ?? prevModelName)
                        : prevModelName}
                    </span>
                  </span>
                )}

                {/* Current name sliding in */}
                <span
                  style={{ gridColumn: 1, gridRow: 1 }}
                  className={
                    isSliding
                      ? slideDirection === 'up'
                        ? 'slide-in-up block'
                        : 'slide-in-down block'
                      : 'block'
                  }
                >
                  {/* displayLabel, if provided, overrides the model name in
                      the trigger. Pass '' to render a blank trigger — used by
                      the NASA SUPIN tab where LLM choice isn't user-facing. */}
                  <span className="text-[#107d40] md:hidden">
                    {displayLabel !== undefined
                      ? displayLabel
                      : (selectedModelConfig?.shortName ??
                        selectedModelConfig?.name)}
                  </span>
                  <span className="hidden text-[#107d40] md:inline">
                    {displayLabel !== undefined
                      ? displayLabel
                      : useShortLabel
                        ? (selectedModelConfig?.shortName ??
                          selectedModelConfig?.name)
                        : selectedModelConfig?.name}
                  </span>
                </span>
              </span>
              <ChevronDown
                className={cn(
                  'ml-1 h-4 w-4 flex-shrink-0 transition-transform duration-200',
                  // Dim the chevron (not the label) to signal a locked dropdown.
                  disabled ? 'opacity-30' : 'opacity-70',
                  isDropdownOpen && 'rotate-180',
                )}
              />
            </Button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <DropdownMenuContent
          className="flex flex-col gap-1 rounded-lg bg-telekinesis-neutral-700 p-1"
          style={{ width: 'var(--radix-dropdown-menu-trigger-width)' }}
          align="end"
          onCloseAutoFocus={(event) => {
            if (openedWithPointerRef.current) {
              event.preventDefault();
              openedWithPointerRef.current = false;
              triggerRef.current?.blur();
            }
          }}
        >
          <DropdownMenuLabel className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-telekinesis-text-secondary">
            Generative Model
          </DropdownMenuLabel>
          {groupedModels.families.map(({ family, models: familyModels }) => (
            <DropdownMenuSub key={family}>
              <DropdownMenuSubTrigger
                className={cn(
                  'cursor-pointer rounded-md bg-telekinesis-neutral-700 px-4 py-3 transition-colors duration-150 focus:bg-[#107d40]/20',
                  familyModels.some((m) => m.id === selectedModel) &&
                    'bg-telekinesis-neutral-800',
                )}
              >
                <span
                  className={cn(
                    'font-medium',
                    focused ? 'text-white' : 'text-telekinesis-text-primary',
                  )}
                >
                  {family}
                </span>
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="flex min-w-[220px] flex-col gap-1 rounded-lg bg-telekinesis-neutral-700 p-1">
                {familyModels.map((model) => {
                  const versionLabel = model.name.startsWith(family)
                    ? model.name.slice(family.length).replace(/^[\s.]+/, '') ||
                      model.name
                    : model.name;
                  return (
                    <DropdownMenuItem
                      key={model.id}
                      className={cn(
                        'cursor-pointer rounded-md bg-telekinesis-neutral-700 px-4 py-3 transition-colors duration-150 focus:bg-[#107d40]/20',
                        selectedModel === model.id &&
                          'bg-telekinesis-neutral-800',
                        !!model.disabled && 'opacity-50',
                      )}
                      onClick={(event) => {
                        onModelChange(model.id);
                        setIsDropdownOpen(false);
                        event.stopPropagation();
                      }}
                      disabled={!!model.disabled}
                    >
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <span
                            className={cn(
                              'font-medium',
                              focused
                                ? 'text-white'
                                : 'text-telekinesis-text-primary',
                            )}
                          >
                            {versionLabel}
                          </span>
                          {model.maxImages != null && model.maxImages > 1 && (
                            <span className="ml-2 shrink-0 text-xs text-gray-500">
                              {model.maxImages >= 99
                                ? '∞'
                                : `≤${model.maxImages}`}{' '}
                              img
                            </span>
                          )}
                        </div>
                        <p
                          className={cn(
                            'mt-0.5 text-left text-xs',
                            focused ? 'text-white' : 'text-gray-400',
                          )}
                        >
                          {model.description}
                        </p>
                      </div>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          ))}
          {groupedModels.standalone.map((model) => (
            <DropdownMenuItem
              key={model.id}
              className={cn(
                'cursor-pointer rounded-md bg-telekinesis-neutral-700 px-4 py-3 transition-colors duration-150 focus:bg-[#107d40]/20',
                selectedModel === model.id && 'bg-telekinesis-neutral-800',
                !!model.disabled && 'opacity-50',
              )}
              onClick={(event) => {
                onModelChange(model.id);
                setIsDropdownOpen(false);
                event.stopPropagation();
              }}
              disabled={!!model.disabled}
            >
              <div className="flex-1">
                <div className="flex items-center">
                  <span
                    className={cn(
                      'font-medium',
                      focused ? 'text-white' : 'text-telekinesis-text-primary',
                    )}
                  >
                    {model.name}
                  </span>
                </div>
                <p
                  className={cn(
                    'mt-0.5 text-left text-xs',
                    focused ? 'text-white' : 'text-gray-400',
                  )}
                >
                  {model.description}
                </p>
              </div>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <TooltipContent>{lockedTooltip ?? 'Select A.I. Model'}</TooltipContent>
    </Tooltip>
  );
}
